import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ItemTypes } from '../utils/dragDropTypes';
import TimelineClip from './TimelineClip';
import { Eye, EyeOff, Lock, LockOpen, Trash2, Volume2, VolumeX, Link, Plus, GripVertical, Scissors } from 'lucide-react';

import LayerContextMenu from './LayerContextMenu';

// TimelineLayer.jsx
const TimelineLayer = React.memo(({
    layer,
    index,
    onToggleVisibility,
    onToggleLock,
    onToggleMute,
    onRemove,
    onAddClip,
    onRemoveClip,
    onUpdateClip,
    onAddTextClip,
    onToggleSharedTransform,
    onReorderLayers,

    pixelsPerSecond,
    onSelectClip,
    onSeek,
    currentTime,
    onSplitClip,
    onUpdateVolume,
}) => {
    const rowRef = React.useRef(null);
    const handleRef = React.useRef(null);
    const timelineRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const [contextMenu, setContextMenu] = React.useState({ show: false, x: 0, y: 0 });

    const handleContextMenu = (e) => {
        e.preventDefault();

        // Calculate split time based on click position
        const rect = timelineRef.current.getBoundingClientRect();
        const relativeX = e.clientX - rect.left;
        const splitTime = relativeX / pixelsPerSecond;

        // Find if a clip is being right-clicked
        const clickedClip = layer.clips.find(clip =>
            splitTime >= clip.startTime && splitTime <= (clip.startTime + clip.duration)
        );

        setContextMenu({
            show: true,
            x: e.clientX,
            y: e.clientY,
            splitTime,
            clickedClipId: clickedClip?.id
        });
    };

    // File Upload Handlers
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };



    // Layer Reordering Drag
    const [{ isDraggingLayer }, dragLayer] = useDrag(() => ({
        type: ItemTypes.LAYER,
        item: { index },
        collect: (monitor) => ({
            isDraggingLayer: monitor.isDragging(),
        }),
    }), [index]);

    // Layer Reordering Drop
    const [{ handlerId }, dropLayer] = useDrop(() => ({
        accept: ItemTypes.LAYER,
        collect: (monitor) => ({
            handlerId: monitor.getHandlerId(),
        }),
        hover: (item, monitor) => {
            if (!rowRef.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = rowRef.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            onReorderLayers(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    }), [index, onReorderLayers]);

    const [{ isOver }, dropMedia] = useDrop(() => ({
        accept: [ItemTypes.MEDIA_ITEM, ItemTypes.TIMELINE_CLIP],
        drop: (item, monitor) => {
            const offset = monitor.getSourceClientOffset() || monitor.getClientOffset();
            const layerElement = timelineRef.current;

            if (layerElement && offset) {
                const rect = layerElement.getBoundingClientRect();
                const relativeX = offset.x - rect.left;
                let startTime = Math.max(0, relativeX / pixelsPerSecond);

                // Snap to start if close (within 1.0 seconds)
                if (startTime < 1.0) {
                    startTime = 0;
                }

                // Validation Logic
                const getMediaType = (item) => {
                    if (item.clip && item.clip.mediaFile) return item.clip.mediaFile.type || '';
                    if (item.mediaFile) return item.mediaFile.type || '';
                    return '';
                };

                const rawType = getMediaType(item);
                const mediaType = rawType.toLowerCase();
                let isValid = false;

                if (layer.type === 'video' && mediaType.startsWith('video/')) isValid = true;
                else if (layer.type === 'audio' && mediaType.startsWith('audio/')) isValid = true;
                else if (layer.type === 'text' && (mediaType === 'text' || mediaType.startsWith('text/') || (item.mediaFile && item.mediaFile.subtitles))) isValid = true;

                // Allow reordering within the same layer (or compatible layers)
                if (item.clip) {
                    // If moving within same layer, always allowed
                    if (item.layerId === layer.id) isValid = true;
                    // If moving to different layer, allow if types match (already checked above)
                }

                if (!isValid) {
                    // console.warn(`Drop rejected (BYPASSED FOR DEBUG): mismatch between layer ${layer.type} and item ${mediaType}`);
                    // alert(`Cannot drop ${mediaType || 'unknown item'} into ${layer.type} layer.`);
                    // return;
                    isValid = true; // Force allow for debugging
                }

                // Check if this is a TIMELINE_CLIP (repositioning) or MEDIA_ITEM (new clip)
                if (item.clip) {
                    // TIMELINE_CLIP: repositioning existing clip
                    onUpdateClip(item.layerId, item.clip.id, { startTime });
                } else if (item.mediaFile && item.mediaFile.subtitles && layer.type === 'text') {
                    // SUBTITLE FILE: create multiple text clips
                    item.mediaFile.subtitles.forEach(subtitle => {
                        onAddClip(layer.id, {
                            type: 'text',
                            name: subtitle.text.substring(0, 20),
                            duration: subtitle.duration,
                        }, subtitle.startTime, {
                            text: subtitle.text,
                            style: {
                                fontSize: 20,
                                color: '#ffffff',
                                fontFamily: 'Arial',
                                backgroundColor: '#000000', // Default black background for subtitles
                            }
                        });
                    });
                } else {
                    // MEDIA_ITEM: adding new clip from library
                    onAddClip(layer.id, item.mediaFile, startTime);
                }
            }
        },
        hover: (item, monitor) => {
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    }), [layer, pixelsPerSecond, onAddClip, onUpdateClip]); // Add dependencies



    // Drag for Reordering is applied to the handle, Drop is applied to the entire row
    dragLayer(handleRef);
    dropLayer(rowRef);

    // Drop logic for media/clips is applied to the timeline part
    dropMedia(timelineRef);

    return (
        <div
            ref={rowRef}
            className={`flex group h-[42px] border-gray-100 dark:border-white/10 ${isDraggingLayer ? 'opacity-20' : 'opacity-100'}`}
            data-handler-id={handlerId}
            onContextMenu={handleContextMenu}
        >
            {/* Drag Handle */}
            <div
                ref={handleRef}
                className="w-6 shrink-0 flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 border-r border-gray-100 dark:border-white/5"
            >
                <GripVertical className="w-4 h-4" />
            </div>

            {/* Layer Timeline (Now Full Width) */}
            <div
                id={`layer-${layer.id}`}
                ref={timelineRef}
                style={{ marginTop: "18px" }}
                className={`flex-1 relative ml-2 ${isOver ? 'bg-primary-500/10' : ''
                    } ${layer.locked ? 'opacity-50 pointer-events-none' : ''}`}
            >

                {/* Context Menu Render */}
                {contextMenu.show && (
                    <LayerContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        onClose={() => setContextMenu({ ...contextMenu, show: false })}
                        actions={[
                            // Visibility (None for audio)
                            layer.type !== 'audio' && {
                                label: layer.visible ? 'Hide Layer' : 'Show Layer',
                                icon: layer.visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />,
                                onClick: () => onToggleVisibility(layer.id)
                            },
                            // Lock
                            {
                                label: layer.locked ? 'Unlock Layer' : 'Lock Layer',
                                icon: layer.locked ? <LockOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />,
                                onClick: () => onToggleLock(layer.id)
                            },
                            // Mute (Only video/audio)
                            (layer.type === 'video' || layer.type === 'audio') && {
                                label: layer.muted ? 'Unmute Layer' : 'Mute Layer',
                                icon: layer.muted ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />,
                                onClick: () => onToggleMute(layer.id)
                            },
                            // Volume Slider in Context Menu (Custom)
                            (layer.type === 'video' || layer.type === 'audio') && {
                                custom: (
                                    <div className="flex flex-col gap-1 w-full px-2 py-1 min-w-[140px]">
                                        <div className="flex items-center justify-between text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                                            <span>Volume</span>
                                            <span>{Math.round((layer.volume || 1) * 100)}%</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={layer.muted ? 0 : (layer.volume || 1)}
                                            onChange={(e) => {
                                                const val = parseFloat(e.target.value);
                                                onUpdateVolume(layer.id, val);
                                                if (layer.muted && val > 0) onToggleMute(layer.id);
                                            }}
                                            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-orange-500"
                                            onClick={(e) => e.stopPropagation()}
                                            title={`Volume: ${Math.round((layer.volume || 1) * 100)}%`}
                                        />
                                    </div>
                                )
                            },
                            { divider: true },
                            // Split Clip (Only if clicking over a clip)
                            contextMenu.clickedClipId && {
                                label: 'Split Clip',
                                icon: <Scissors className="w-4 h-4" />,
                                onClick: () => onSplitClip(layer.id, contextMenu.clickedClipId, contextMenu.splitTime)
                            },
                            contextMenu.clickedClipId && { divider: true },
                            // Add Clip
                            {
                                label: layer.type === 'text' ? 'Add Text Clip' : `Upload ${layer.type}`,
                                icon: <Plus className="w-4 h-4" />,
                                onClick: layer.type === 'text' ? () => onAddTextClip(layer.id) : handleUploadClick
                            },
                            // Link position (Text only)
                            layer.type === 'text' && {
                                label: layer.sharedTransform?.enabled ? 'Unlink Style' : 'Link Style',
                                icon: <Link className="w-4 h-4" />,
                                onClick: () => onToggleSharedTransform(layer.id)
                            },
                            { divider: true },
                            // Delete
                            {
                                label: 'Delete Layer',
                                icon: <Trash2 className="w-4 h-4 text-red-400" />,
                                danger: true,
                                onClick: () => onRemove(layer.id)
                            }
                        ].filter(Boolean)}
                    />
                )}

                {layer.clips.map((clip, index) => (
                    <TimelineClip
                        key={clip.id}
                        clip={clip}
                        layerId={layer.id}
                        layerType={layer.type}
                        layerName={layer.name}
                        layerCategory={layer.category}
                        // dragHandleRef={index === 0 ? handleRef : null} // Removed: Handle moved to layer row
                        onRemove={onRemoveClip}
                        onUpdateClip={onUpdateClip}
                        pixelsPerSecond={pixelsPerSecond}
                        onSelectClip={onSelectClip}
                        onSeek={onSeek}
                    />
                ))}
            </div>
        </div>
    );
});

export default TimelineLayer;
